import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import {
  Project,
  Room,
  NoteWithDetails,
  getProjects,
  getRooms,
  getProjectNotes,
  deleteNote,
  getNotePhotoUrl
} from '../../api/client';
import { EditNoteModal } from '../../components/EditNoteModal';
import { PhotoLightbox } from '../../components/PhotoLightbox';

export function NotesModeration() {
  const { projectId } = useParams<{ projectId: string }>();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [notes, setNotes] = useState<NoteWithDetails[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [roomFilter, setRoomFilter] = useState<number | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);

  const [selectedNote, setSelectedNote] = useState<NoteWithDetails | null>(null);
  const [lightboxPhotoUrl, setLightboxPhotoUrl] = useState<string | null>(null);

  const loadNotes = async () => {
    if (!token || !projectId) return;
    try {
      const notesData = await getProjectNotes(token, parseInt(projectId));
      setNotes(notesData);
    } catch (error) {
      console.error('Failed to reload notes:', error);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      if (!token || !projectId) return;
      try {
        const [projects, notesData, roomsData] = await Promise.all([
          getProjects(token),
          getProjectNotes(token, parseInt(projectId)),
          getRooms(token, parseInt(projectId))
        ]);

        const found = projects.find(p => p.id === parseInt(projectId));
        if (found) {
          setProject(found);
          setNotes(notesData);
          setRooms(roomsData);
        } else {
          navigate('/admin');
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, token, isAuthenticated, navigate]);

  const filteredNotes = notes.filter(note => {
    if (roomFilter !== null && !note.rooms.some(r => r.room_id === roomFilter)) return false;
    return true;
  });

  const handleDeleteNote = async (noteId: number) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    setDeletingNoteId(noteId);
    try {
      await deleteNote(token, noteId);
      setNotes(notes.filter(n => n.id !== noteId));
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated || loading) {
    return (
      <div style={{ fontFamily: 'system-ui', padding: '24px' }}>
        Loading...
      </div>
    );
  }

  if (!project) {
    return null;
  }

  return (
    <div style={{ fontFamily: 'system-ui' }}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate(`/admin/projects/${projectId}`)}
            style={{
              background: 'transparent',
              border: '1px solid #fff',
              color: '#fff',
              padding: '8px 16px',
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            Back to Project
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px' }}>
              Notes Moderation
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
              {project.name}
            </p>
          </div>
        </div>
      </header>

      <PendingUsersBanner />

      <main style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Filters */}
        <div style={{
          background: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '14px' }}>
                Filter by Room
              </label>
              <select
                value={roomFilter === null ? '' : roomFilter}
                onChange={(e) => setRoomFilter(e.target.value ? parseInt(e.target.value) : null)}
                style={{ width: '100%', padding: '8px', fontSize: '14px' }}
              >
                <option value="">All Rooms</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>
                    Room {room.number}{room.name ? ` - ${room.name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{
            padding: '10px 16px',
            background: '#fff',
            borderRadius: '4px',
            fontWeight: 'bold',
            display: 'inline-block'
          }}>
            {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Notes Display */}
        {filteredNotes.length === 0 ? (
          <div style={{
            background: '#fff',
            padding: '40px',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#666'
          }}>
            <p style={{ margin: 0, fontSize: '16px' }}>
              No notes found{roomFilter !== null ? ' for this room' : ''}.
            </p>
          </div>
        ) : (
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #ddd'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd',
                    width: '120px'
                  }}>
                    Room
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd'
                  }}>
                    Note Content
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd',
                    width: '120px'
                  }}>
                    Author
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd',
                    width: '180px'
                  }}>
                    Created
                  </th>
                  <th style={{
                    padding: '12px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #ddd',
                    width: '100px'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredNotes.map(note => (
                  <tr
                    key={note.id}
                    onClick={() => setSelectedNote(note)}
                    style={{
                      borderBottom: '1px solid #eee',
                      cursor: 'pointer'
                    }}
                  >
                    <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                      {note.rooms.map((room, idx) => (
                        <div key={room.room_id}>
                          <div style={{ fontWeight: 'bold' }}>
                            {room.room_number}
                          </div>
                          {room.room_name && (
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {room.room_name}
                            </div>
                          )}
                          {idx < note.rooms.length - 1 && (
                            <div style={{ margin: '4px 0', borderBottom: '1px solid #eee' }} />
                          )}
                        </div>
                      ))}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        {note.photo_url && (
                          <img
                            src={getNotePhotoUrl(note.photo_url) || ''}
                            alt="Note photo thumbnail"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxPhotoUrl(getNotePhotoUrl(note.photo_url));
                            }}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              flexShrink: 0
                            }}
                          />
                        )}
                        <div style={{
                          maxWidth: '500px',
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {note.content}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {note.author_name}
                    </td>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                      {formatDate(note.created_at)}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                       <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNote(note.id);
                        }}
                        disabled={deletingNoteId === note.id}
                        style={{
                          background: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          padding: '6px 12px',
                          cursor: deletingNoteId === note.id ? 'wait' : 'pointer',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          opacity: deletingNoteId === note.id ? 0.6 : 1
                        }}
                      >
                        {deletingNoteId === note.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <EditNoteModal
        note={selectedNote}
        isOpen={selectedNote !== null}
        onClose={() => setSelectedNote(null)}
        onSave={loadNotes}
        token={token || ''}
      />

      <PhotoLightbox
        photoUrl={lightboxPhotoUrl}
        isOpen={lightboxPhotoUrl !== null}
        onClose={() => setLightboxPhotoUrl(null)}
      />
    </div>
  );
}
